import React, { useEffect, useState } from 'react';
import { Linkedin, Mail, ArrowDown, MapPin } from 'lucide-react';
import { PROFILE, TOPICS } from '../../data/mock';
import { useIsMobile, useIsTablet } from '../../hooks/useWindowSize';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);
  const [topicIndex, setTopicIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => { setTopicIndex(prev => (prev + 1) % TOPICS.length); setFade(true); }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollToAbout = () => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="home" style={{
      background: '#0A0A0A', minHeight: '100vh',
      display: 'flex', alignItems: 'center',
      position: 'relative', overflow: 'hidden',
      paddingTop: isMobile ? '68px' : '80px'
    }}>
      {/* Grid texture */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.022) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.022) 1px, transparent 1px)',
        backgroundSize: '64px 64px', pointerEvents: 'none'
      }} />
      {/* Red left bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#E62B1E' }} />

      <div style={{
        maxWidth: '1280px', margin: '0 auto',
        padding: isMobile ? '40px 20px 60px' : isTablet ? '60px 32px' : '60px 48px',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 340px' : '1fr 420px',
        gap: isMobile ? '40px' : '80px',
        alignItems: 'center', width: '100%',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.9s ease, transform 0.9s ease'
      }}>

        {/* Left text - on mobile show photo first */}
        {isMobile && (
          <div style={{ position: 'relative', order: -1 }}>
            <div style={{ borderRadius: '6px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.7)' }}>
              <img src={PROFILE.photos.hero} alt="Krupal Chaudhary"
                style={{ width: '100%', height: '380px', objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '100px',
                background: 'linear-gradient(transparent, rgba(10,10,10,0.75))'
              }} />
            </div>
            <div style={{
              position: 'absolute', bottom: '-16px', left: '16px',
              background: '#E62B1E', borderRadius: '4px', padding: '8px 16px', zIndex: 2
            }}>
              <p style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '12px', color: '#fff', letterSpacing: '2px', margin: 0 }}>TEDx · SRMIST · 2026</p>
            </div>
          </div>
        )}

        {/* Text content */}
        <div style={{ marginTop: isMobile ? '20px' : 0 }}>
          {/* TEDx badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(230,43,30,0.1)', border: '1px solid rgba(230,43,30,0.3)',
            borderRadius: '4px', padding: '5px 12px', marginBottom: '24px'
          }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#E62B1E' }} />
            <span style={{
              fontFamily: 'Inter, sans-serif', fontSize: isMobile ? '9px' : '11px',
              fontWeight: 700, color: '#E62B1E', letterSpacing: '1.5px', textTransform: 'uppercase'
            }}>TEDx Speaker · SRM University · 24 March 2026</span>
          </div>

          {/* Name */}
          <h1 style={{
            fontFamily: '"Bebas Neue", sans-serif',
            fontSize: isMobile ? '64px' : isTablet ? '72px' : 'clamp(56px, 7vw, 96px)',
            fontWeight: 400, color: '#FFFFFF',
            lineHeight: '0.95', letterSpacing: '2px', margin: '0 0 16px 0'
          }}>
            KRUPAL<br /><span style={{ color: '#E62B1E' }}>CHAUDHARY</span>
          </h1>

          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: isMobile ? '14px' : '17px',
            color: '#8A8A8A', fontWeight: 400, margin: '0 0 16px 0'
          }}>Entrepreneur · Author · AI Product Builder · Digital Transformation Leader</p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '28px', flexWrap: 'wrap' }}>
            <MapPin size={13} color="#E62B1E" />
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#5A5A5A' }}>Ahmedabad, India</span>
            <span style={{ color: '#2A2A2A', margin: '0 4px' }}>·</span>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#5A5A5A' }}>Co-founder & CEO, Demaze Tech Studio</span>
          </div>

          {/* Tagline */}
          <div style={{ borderLeft: '3px solid #E62B1E', paddingLeft: '18px', marginBottom: '32px' }}>
            <p style={{
              fontFamily: '"Bebas Neue", sans-serif',
              fontSize: isMobile ? '16px' : 'clamp(18px, 2.2vw, 26px)',
              color: '#CCCCCC', letterSpacing: '1px', margin: 0, lineHeight: 1.4
            }}>"{PROFILE.tagline}"</p>
          </div>

          {/* Rotating topic */}
          <div style={{ marginBottom: '36px' }}>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#4A4A4A', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 }}>Speaking on -</span>
            <p style={{
              fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#D0D0D0',
              fontWeight: 500, margin: '6px 0 0 0', minHeight: '22px',
              opacity: fade ? 1 : 0, transition: 'opacity 0.3s ease'
            }}>{TOPICS[topicIndex]}</p>
          </div>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a href={`mailto:${PROFILE.email}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                background: '#E62B1E', color: '#FFFFFF',
                padding: isMobile ? '12px 22px' : '14px 28px', borderRadius: '4px',
                fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600,
                textDecoration: 'none', transition: 'background 0.2s ease, transform 0.2s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#C42318'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#E62B1E'; e.currentTarget.style.transform = 'translateY(0)'; }}
            ><Mail size={13} /> Get in Touch</a>
            <a href={PROFILE.linkedin} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                background: 'transparent', color: '#CCCCCC',
                padding: isMobile ? '11px 22px' : '13px 28px', borderRadius: '4px',
                border: '1px solid #2E2E2E',
                fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'border-color 0.2s ease, transform 0.2s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#E62B1E'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2E2E2E'; e.currentTarget.style.transform = 'translateY(0)'; }}
            ><Linkedin size={13} /> LinkedIn</a>
          </div>
        </div>

        {/* Right photo - desktop/tablet only */}
        {!isMobile && (
          <div style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute', top: '-16px', right: '-16px',
              width: '100%', height: '100%',
              border: '2px solid rgba(230,43,30,0.25)',
              borderRadius: '6px', zIndex: 0
            }} />
            <div style={{ position: 'relative', zIndex: 1, borderRadius: '6px', overflow: 'hidden', boxShadow: '0 40px 80px rgba(0,0,0,0.7)' }}>
              <img src={PROFILE.photos.hero} alt="Krupal Chaudhary"
                style={{ width: '100%', height: isTablet ? '440px' : '520px', objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '120px',
                background: 'linear-gradient(transparent, rgba(10,10,10,0.75))'
              }} />
            </div>
            <div style={{
              position: 'absolute', bottom: '-20px', left: '24px',
              background: '#E62B1E', borderRadius: '4px', padding: '10px 20px', zIndex: 2,
              boxShadow: '0 8px 24px rgba(230,43,30,0.35)'
            }}>
              <p style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '13px', color: '#fff', letterSpacing: '2px', margin: 0 }}>TEDx · SRMIST · 2026</p>
            </div>
          </div>
        )}
      </div>

      {/* Scroll cue */}
      {!isMobile && (
        <button onClick={scrollToAbout} style={{
          position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'
        }}>
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3A3A' }}>Scroll</span>
          <ArrowDown size={16} color="#E62B1E" />
        </button>
      )}
    </section>
  );
};

export default HeroSection;
